/**
 * Phase 7.1c — Offline document upload queue using IndexedDB.
 * Queues file uploads when offline and syncs them when connectivity is restored.
 *
 * Usage:
 *   import { queueUpload, processQueue, getPendingUploads } from './offlineUploadQueue';
 *
 *   // In your upload handler:
 *   if (!navigator.onLine) {
 *     await queueUpload(file, '/api/network/my-documents/', metadata);
 *   }
 *
 *   // The queue auto-processes when back online, but you can also trigger manually:
 *   await processQueue();
 */

const DB_NAME = 'biztimize-offline';
const DB_VERSION = 1;
const STORE_NAME = 'upload-queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Queue a file upload for later sync.
 * @param {File} file - The file to upload
 * @param {string} url - API endpoint URL
 * @param {Object} metadata - Additional form data fields
 * @returns {Promise<number>} Queue entry ID
 */
export async function queueUpload(file, url, metadata = {}) {
  const db = await openDB();

  // Read file as ArrayBuffer for storage in IndexedDB
  const arrayBuffer = await file.arrayBuffer();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    const entry = {
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      fileData: arrayBuffer,
      url,
      metadata,
      status: 'pending', // pending | uploading | completed | failed
      attempts: 0,
      maxAttempts: 5,
      error: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const request = store.add(entry);
    request.onsuccess = () => {
      resolve(request.result);
      // Register for Background Sync if available
      registerBackgroundSync();
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get all pending uploads.
 * @returns {Promise<Array>} List of queued uploads (without file data for performance)
 */
export async function getPendingUploads() {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      // Strip fileData from results to reduce memory usage
      const results = request.result.map(({ fileData, ...rest }) => rest);
      resolve(results.filter((r) => r.status !== 'completed'));
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Process the upload queue — retry all pending/failed uploads.
 * @param {function} onProgress - Optional callback: (id, status) => void
 * @returns {Promise<{succeeded: number, failed: number}>}
 */
export async function processQueue(onProgress) {
  if (!navigator.onLine) return { succeeded: 0, failed: 0 };

  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const store = tx.objectStore(STORE_NAME);
  const allEntries = await promisifyRequest(store.getAll());

  const pending = allEntries.filter(
    (e) => e.status === 'pending' || (e.status === 'failed' && e.attempts < e.maxAttempts)
  );

  let succeeded = 0;
  let failed = 0;

  for (const entry of pending) {
    await updateEntry(db, entry.id, { status: 'uploading', updatedAt: new Date().toISOString() });
    onProgress?.(entry.id, 'uploading');

    try {
      // Reconstruct File from stored ArrayBuffer
      const file = new File([entry.fileData], entry.fileName, { type: entry.fileType });

      const formData = new FormData();
      formData.append('file', file);
      Object.entries(entry.metadata).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Get auth token from localStorage (same as api.js interceptor)
      const token = localStorage.getItem('access_token');

      const response = await fetch(entry.url, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (response.ok) {
        await updateEntry(db, entry.id, {
          status: 'completed',
          updatedAt: new Date().toISOString(),
        });
        onProgress?.(entry.id, 'completed');
        succeeded++;
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      const attempts = entry.attempts + 1;
      await updateEntry(db, entry.id, {
        status: attempts >= entry.maxAttempts ? 'failed' : 'pending',
        attempts,
        error: err.message,
        updatedAt: new Date().toISOString(),
      });
      onProgress?.(entry.id, 'failed');
      failed++;
    }
  }

  return { succeeded, failed };
}

/**
 * Remove a completed or permanently failed entry from the queue.
 */
export async function removeEntry(id) {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.objectStore(STORE_NAME).delete(id);
}

/**
 * Clear all completed entries.
 */
export async function clearCompleted() {
  const db = await openDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  const all = await promisifyRequest(store.getAll());

  for (const entry of all) {
    if (entry.status === 'completed') {
      store.delete(entry.id);
    }
  }
}

// ── Internal helpers ────────────────────────────────────────

async function updateEntry(db, id, updates) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(id);

    getReq.onsuccess = () => {
      const entry = { ...getReq.result, ...updates };
      const putReq = store.put(entry);
      putReq.onsuccess = () => resolve();
      putReq.onerror = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function registerBackgroundSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.sync.register('offline-upload-sync').catch(() => {
        // Background Sync not supported (e.g., Safari) — fall back to online listener
      });
    });
  }
}

// ── Auto-sync when connectivity is restored ─────────────────

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    processQueue().then(({ succeeded, failed }) => {
      if (succeeded > 0) {
        console.info(`Offline queue: ${succeeded} uploads synced, ${failed} failed`);
      }
    });
  });
}
